import React, { useMemo } from 'react';
import { Spin } from 'antd';
import moment from 'moment';
import {
	CHART_PRIMARY_COLOR,
	COMPARISON_PERIOD_COLOR,
} from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsConstants';
import {
	buildMetricTooltipHtml,
	extractSpendValue,
	parseSafeFloat,
} from '../life_cycle_analytics_overview_page/LifeCycleAnalyticsUtils';
import { buildSeriesComparisonTooltipHtml } from '../../utils/AnalyticsComparisonTooltipUtils';
import {
	COMPARISON_MODE,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_CURRENT,
	getAnalyticsRolePrimaryColor,
} from '../../utils/AnalyticsComparisonUtils';
import ComparisonLegend from '../analytics_comparison/ComparisonLegend';
import {
	AnalyticsChartActions,
	AnalyticsChartCard,
	AnalyticsChartHeader,
	pairComparisonByMonth,
} from '../analytics_charts';
import SpendBarChart from '../analytics_charts/SpendBarChart';
import { useAnalyticsBreakdownComparison } from '../../hooks/useAnalyticsBreakdownComparison';
import { EmptyState } from '../empty_state/EmptyState';
import { toMonthChartRow } from '../../utils/DataFormatterUtils';
import { exportMonthlySeriesCsv } from '../../utils/AnalyticsBreakdownExportUtils';

export type KPISpendChartProps = {
	data: any[];
	loading: boolean;
	filters: Record<string, any>;
	fetchComparisonData: (params: Record<string, any>) => Promise<any[]>;
	userType: string;
	currency: {
		id: string;
		format: (val: number, decimals?: number) => string;
		symbolFormat: (val: number, decimals?: number) => string;
	};
};

const resolveSpendValue = (row: any) => {
	if (row && row.monthlyTotal !== null && row.monthlyTotal !== undefined) {
		return parseSafeFloat(row.monthlyTotal);
	}
	return extractSpendValue(row);
};

const toMonthRow = (row: any) => ({
	...toMonthChartRow(row),
	spendValue: resolveSpendValue(row),
});

const KPISpendChart = ({
	data,
	loading,
	filters,
	fetchComparisonData,
	userType,
	currency,
}: KPISpendChartProps) => {
	const currentColor = getAnalyticsRolePrimaryColor(userType) || CHART_PRIMARY_COLOR;
	const previousColor = COMPARISON_PERIOD_COLOR;

	const {
		comparisonState,
		setComparison,
		comparisonRows,
		comparisonLoading,
		selectedStart,
		selectedEnd,
		comparisonRange,
		comparisonDisabled,
		comparisonDisabledReason,
		hasComparison,
	} = useAnalyticsBreakdownComparison({
		filters,
		fetchComparisonData,
	});

	const currentRows = useMemo(() => (data || []).map(toMonthRow), [data]);
	const previousRows = useMemo(() => (comparisonRows || []).map(toMonthRow), [comparisonRows]);

	const chartData = useMemo(() => {
		const paired = pairComparisonByMonth({
			current: currentRows,
			previous: previousRows,
			hasComparison,
			selectedRange: selectedStart && selectedEnd ? [selectedStart, selectedEnd] : null,
			comparisonRange,
			buildPreviousRow: (prevRow: any, curRow: any) => ({
				...(prevRow || {}),
				previousMonthLabel: prevRow ? prevRow.monthLabel : '',
				previousMonthFull: prevRow ? prevRow.monthFull : '',
				monthLabel: curRow.monthLabel,
			}),
		});
		return [...paired.previous, ...paired.current];
	}, [currentRows, previousRows, hasComparison, selectedStart, selectedEnd, comparisonRange]);

	const formatSpend = (val: any) => {
		const num = parseSafeFloat(val, NaN);
		return currency.format(isNaN(num) ? 0 : num, 0);
	};

	const totalSpend = useMemo(
		() => currentRows.reduce((sum: number, row: any) => sum + parseSafeFloat(row.spendValue), 0),
		[currentRows]
	);

	const isCurrentItem = (item: any) =>
		!!(item && item.data && item.data[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_CURRENT);

	const renderComparisonTooltip = (title: string, items: any[]) => {
		if (!items || items.length === 0) return '';
		const rows = items
			.slice()
			.sort((a, b) => Number(isCurrentItem(b)) - Number(isCurrentItem(a)))
			.map((item) => {
				const isCurrent = isCurrentItem(item);
				const labelText = isCurrent
					? item.data && (item.data.monthFull || item.data.monthLabel)
					: item.data &&
					  (item.data.previousMonthFull || item.data.previousMonthLabel || item.data.monthFull);
				return {
					label: labelText || title,
					color: isCurrent ? currentColor : previousColor,
					value: formatSpend(item.data && item.data.spendValue),
				};
			});
		return buildSeriesComparisonTooltipHtml(rows);
	};

	const intervalTooltipDefinition: [string, (...args: any[]) => { name: string; value: string }] = [
		'monthFull*spendValue',
		(monthFull: any, spendValue: any) => ({
			name: 'Spend',
			value: buildMetricTooltipHtml(monthFull, 'Spend', formatSpend(spendValue), currentColor),
		}),
	];

	const comparisonStart: moment.Moment | null = comparisonRange ? comparisonRange[0] : null;
	const comparisonEnd: moment.Moment | null = comparisonRange ? comparisonRange[1] : null;

	const title = <AnalyticsChartHeader label="Spend" value={currency.format(totalSpend, 0)} />;

	const handleDownload = () =>
		exportMonthlySeriesCsv({
			filename: 'Spend',
			valueHeader: 'Spend',
			currentRows,
			previousRows,
			includeComparison: hasComparison && previousRows.length > 0,
			formatValue: (row) => formatSpend(row.spendValue),
		});

	const extra = (
		<AnalyticsChartActions
			comparison={{
				value: comparisonState || { mode: COMPARISON_MODE.NONE },
				onChange: setComparison,
				selectedStart,
				selectedEnd,
				isTimeBased: true,
				loading: !!comparisonLoading,
				disabled: !!comparisonDisabled,
				disabledReason: comparisonDisabled ? comparisonDisabledReason : undefined,
			}}
			onDownload={handleDownload}
		/>
	);

	const hasData = currentRows.length > 0;

	return (
		<AnalyticsChartCard title={title} extra={extra} className="h-full">
			<Spin spinning={loading || comparisonLoading}>
				{!hasData && !loading && !comparisonLoading ? (
					<EmptyState
						height={300}
						headline={'KPI data not available'}
						body={<div style={{ maxWidth: 488, textAlign: 'center' }}></div>}
					/>
				) : (
					<div className="w-full min-w-0">
						<SpendBarChart
							data={chartData}
							hasComparison={hasComparison}
							currentColor={currentColor}
							previousColor={previousColor}
							axisYTitle={`Facility Costs (${currency.id})`}
							formatYAxisLabel={formatSpend}
							intervalTooltipDefinition={intervalTooltipDefinition}
							renderComparisonTooltip={renderComparisonTooltip}
							height={300}
						/>
						{hasComparison ? (
							<ComparisonLegend
								currentStart={selectedStart}
								currentEnd={selectedEnd}
								previousStart={comparisonStart}
								previousEnd={comparisonEnd}
								currentColor={currentColor}
								previousColor={previousColor}
								mode={comparisonState.mode}
								swatchType="square"
							/>
						) : (
							<div className="mt-3 h-5" aria-hidden="true" />
						)}
					</div>
				)}
			</Spin>
		</AnalyticsChartCard>
	);
};

export default KPISpendChart;
